// ✅ src/pages/VehicleV2/FuelConsumption.jsx  (UPDATED FULL FILE)
// Flow:
// 1) Listing row click -> navigate("/vehicle/FuelConsumption", { state:{ vehicleId, type:"Own"|"Hire" } })
// 2) Entry page reads location.state OR localStorage -> calls API init -> shows fields
// 3) Search -> calls API search (with fromDate/toDate optional)
// 4) Submit -> calls API submit
//
// Backend expected:
//   app.use("/api/fuelConsumption", fuelConsumption);
//
// APIs used:
//   GET  http://nissi-india.com/api/fuelConsumption/init?vehicleId=&type=Own|Hire&loginUserId=&branchId=
//   GET  http://nissi-india.com/api/fuelConsumption/search?vehicleId=&type=Own|Hire&fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
//   POST http://nissi-india.com/api/fuelConsumption/submit
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + searching + submitting states
// - safe init / search / submit handling
// - consistent card / header / button style
// - cleaner reset / validation / navigation handling

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaRedo,
  FaPaperPlane,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/fuelConsumption";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? Math.trunc(x) : def;
};

const toNum = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isoToDMY = (iso) => {
  if (!iso) return "";
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const dmyToISO = (dmy) => {
  const m = String(dmy || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${String(m[2]).padStart(2, "0")}-${String(m[1]).padStart(
    2,
    "0"
  )}`;
};

const FuelConsumption = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ---- vehicle info from listing / localStorage ----
  const vehicleId = useMemo(
    () =>
      location?.state?.vehicleId ||
      localStorage.getItem("FuelConsumptionVehicleId") ||
      "",
    [location?.state?.vehicleId]
  );

  const initialType = useMemo(
    () =>
      location?.state?.type ||
      localStorage.getItem("FuelConsumptionType") ||
      "Own",
    [location?.state?.type]
  );

  const [vehicleType, setVehicleType] = useState(
    String(initialType).toLowerCase().includes("hire") ? "Hire" : "Own"
  );

  // ---- inputs ----
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transportQtyM3, setTransportQtyM3] = useState("");

  // ---- states ----
  const [pageLoading, setPageLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [canSubmit, setCanSubmit] = useState(true);

  const [vehicle, setVehicle] = useState({
    vehicleRegNo: "",
    fuelTypeName: "",
    fuelTypeId: 0,
    vehicleBasisType: "",
  });

  const [result, setResult] = useState({
    startingDate: "",
    endingDate: "",
    noOfLoads: 0,
    noOfKm: 0,
    noOfHrs: 0,
    fuelRate: 0,
    fuelQty: 0,
    fuelTotalAmount: 0,
    avgLead: 0,
  });

  const isBusy = pageLoading || searching || submitting;

  const computed = useMemo(() => {
    const fuelQty = toNum(result.fuelQty, 0);
    const km = toNum(result.noOfKm, 0);
    const hrs = toNum(result.noOfHrs, 0);

    return {
      fuelConsumPerKm: km > 0 ? fuelQty / km : 0,
      fuelConsumPerHrs: hrs > 0 ? fuelQty / hrs : 0,
    };
  }, [result]);

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const resultPanelClass = useMemo(
    () =>
      `rounded-xl border p-6 ${
        isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `text-sm ${isDark ? "text-gray-200/80" : "text-gray-600"}`,
    [isDark]
  );

  const valueClass = useMemo(
    () => `text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const Row = ({ label, value, hint }) => (
    <div className="flex items-center justify-between gap-6 py-2">
      <div className={`${labelClass} w-[46%]`}>{label}</div>
      <div className={`${valueClass} w-[54%] text-left`}>
        {value}
        {hint ? (
          <span
            className={`ml-2 text-xs ${
              isDark ? "text-gray-300/70" : "text-gray-500"
            }`}
          >
            {hint}
          </span>
        ) : null}
      </div>
    </div>
  );

  const resetResult = useCallback(() => {
    setResult({
      startingDate: "",
      endingDate: "",
      noOfLoads: 0,
      noOfKm: 0,
      noOfHrs: 0,
      fuelRate: 0,
      fuelQty: 0,
      fuelTotalAmount: 0,
      avgLead: 0,
    });
  }, []);

  const validateVehicle = useCallback(() => {
    if (!clean(vehicleId)) {
      toast.warn("Vehicle not selected. Please open from listing and click a row.");
      return false;
    }
    return true;
  }, [vehicleId]);

  const validateDateRange = useCallback(() => {
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      toast.warn("To Date must be greater than or equal to From Date");
      return false;
    }
    return true;
  }, [fromDate, toDate]);

  const syncVehicleAndResultFromResponse = useCallback((data) => {
    setCanSubmit(Boolean(data?.canSubmit ?? true));

    setVehicle({
      vehicleRegNo: data?.vehicle?.vehicleRegNo || "",
      fuelTypeName:
        data?.vehicle?.fuelTypeName || data?.vehicle?.fuelType || "",
      fuelTypeId: toInt(data?.vehicle?.fuelTypeId, 0),
      vehicleBasisType: data?.vehicle?.vehicleBasisType || "",
    });

    const startISO =
      data?.period?.fromDateISO || data?.period?.startingDateISO || "";
    const endISO =
      data?.period?.toDateISO || data?.period?.endingDateISO || "";

    const startDMY = data?.period?.startingDate || "";
    const endDMY = data?.period?.endingDate || "";

    if (startISO) setFromDate(startISO);
    else if (startDMY) setFromDate(dmyToISO(startDMY));

    if (endISO) setToDate(endISO);
    else if (endDMY) setToDate(dmyToISO(endDMY));

    setResult({
      startingDate: startDMY || (startISO ? isoToDMY(startISO) : ""),
      endingDate: endDMY || (endISO ? isoToDMY(endISO) : ""),
      noOfLoads: toNum(data?.result?.noOfLoads, 0),
      noOfKm: toNum(data?.result?.noOfKm, 0),
      noOfHrs: toNum(data?.result?.noOfHrs, 0),
      fuelRate: toNum(data?.result?.fuelRate, 0),
      fuelQty: toNum(data?.result?.fuelQty, 0),
      fuelTotalAmount: toNum(data?.result?.fuelTotalAmount, 0),
      avgLead: toNum(data?.result?.avgLead, 0),
    });
  }, []);

  /* =========================================================
     INIT
     ========================================================= */
  const loadInit = useCallback(async () => {
    const ok = validateVehicle();
    if (!ok) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          type: vehicleType,
          loginUserId,
          branchId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      syncVehicleAndResultFromResponse(data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [
    validateVehicle,
    vehicleId,
    vehicleType,
    loginUserId,
    branchId,
    commonHeaders,
    syncVehicleAndResultFromResponse,
  ]);

  /* =========================================================
     SEARCH
     ========================================================= */
  const doSearch = useCallback(async () => {
    const okVehicle = validateVehicle();
    if (!okVehicle) return;

    const okDate = validateDateRange();
    if (!okDate) return;

    try {
      setSearching(true);

      const { data } = await axios.get(`${API_BASE}/search`, {
        params: {
          vehicleId,
          type: vehicleType,
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Search failed");
        return;
      }

      syncVehicleAndResultFromResponse(data);
      toast.success("Search completed");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Search failed");
    } finally {
      setSearching(false);
    }
  }, [
    validateVehicle,
    validateDateRange,
    vehicleId,
    vehicleType,
    fromDate,
    toDate,
    commonHeaders,
    syncVehicleAndResultFromResponse,
  ]);

  /* =========================================================
     SUBMIT
     ========================================================= */
  const doSubmit = useCallback(async () => {
    if (!canSubmit) {
      toast.warn("Submit not allowed (approved / no rights).");
      return;
    }

    const okVehicle = validateVehicle();
    if (!okVehicle) return;

    if (!fromDate || !toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }

    const okDate = validateDateRange();
    if (!okDate) return;

    try {
      setSubmitting(true);

      const payload = {
        vehicleId,
        type: vehicleType,
        loginUserId,
        branchId,
        transportQtyM3: clean(transportQtyM3),
        fromDate,
        toDate,
        startingDateDMY: isoToDMY(fromDate),
        endingDateDMY: isoToDMY(toDate),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Submit failed");
        return;
      }

      toast.success(data?.message || "Saved Successfully");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    validateVehicle,
    fromDate,
    toDate,
    validateDateRange,
    vehicleId,
    vehicleType,
    loginUserId,
    branchId,
    transportQtyM3,
    commonHeaders,
  ]);

  const doReset = useCallback(() => {
    setTransportQtyM3("");
    setFromDate("");
    setToDate("");
    resetResult();
    loadInit();
  }, [resetResult, loadInit]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    if (vehicleId) {
      localStorage.setItem("FuelConsumptionVehicleId", String(vehicleId));
    }
    localStorage.setItem("FuelConsumptionType", vehicleType);
  }, [vehicleId, vehicleType]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">FUEL CONSUMPTION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Fuel Consumption
              </h1>

              <span className={pillClass}>INFRA SCREEN : B-18/1</span>

              <span className={labelClass}>
                Vehicle ID:{" "}
                <span className={valueClass}>{vehicleId || "-"}</span>
              </span>

              <span className={labelClass}>
                Vehicle RegNo:{" "}
                <span className={valueClass}>
                  {vehicle.vehicleRegNo || "-"}
                </span>
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : searching
                    ? "Searching..."
                    : submitting
                    ? "Submitting..."
                    : "Processing..."}
                </span>
              )}

              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          {/* Vehicle Type */}
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className={labelClass}>Vehicle Type</div>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehType"
                  checked={vehicleType === "Hire"}
                  onChange={() => setVehicleType("Hire")}
                  disabled={isBusy}
                />
                <span className={isDark ? "text-gray-200" : "text-gray-700"}>
                  Hire Vehicle
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehType"
                  checked={vehicleType === "Own"}
                  onChange={() => setVehicleType("Own")}
                  disabled={isBusy}
                />
                <span className={isDark ? "text-gray-200" : "text-gray-700"}>
                  Own Vehicle
                </span>
              </label>

              <div className="md:ml-auto text-xs">
                <span className={`${pillClass} ${canSubmit ? "" : "opacity-70"}`}>
                  {canSubmit ? "Submit Enabled" : "Submit Disabled"}
                </span>
              </div>
            </div>
          </div>

          {/* Search area */}
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3 className="text-lg font-semibold mb-4 uppercase">
              Search Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div>
                <div className={labelClass}>From Date</div>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={inputClass}
                  disabled={isBusy}
                />
              </div>

              <div className="hidden md:flex justify-center">
                <button
                  type="button"
                  onClick={doSearch}
                  disabled={isBusy}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-7 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {searching ? "Searching..." : "Search"}
                </button>
              </div>

              <div>
                <div className={labelClass}>To Date</div>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={inputClass}
                  disabled={isBusy}
                />
              </div>

              <div className="md:hidden">
                <button
                  type="button"
                  onClick={doSearch}
                  disabled={isBusy}
                  className={`w-full flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-7 py-2.5 rounded-lg font-semibold shadow-lg ${
                    isBusy ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {searching ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className={labelClass}>Transport Qty (M3)</div>
                <input
                  type="text"
                  value={transportQtyM3}
                  onChange={(e) => setTransportQtyM3(e.target.value)}
                  className={inputClass}
                  placeholder="Enter transport quantity"
                  disabled={isBusy}
                />
              </div>

              <div className="md:col-span-2 flex items-end">
                <div className={labelClass}>
                  Fuel Type:{" "}
                  <span className={valueClass}>
                    {vehicle.fuelTypeName || "-"}
                  </span>
                  {vehicle.vehicleBasisType ? (
                    <>
                      {" "}
                      • Basis:{" "}
                      <span className={valueClass}>
                        {vehicle.vehicleBasisType}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Result Area */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* LEFT */}
            <div className={resultPanelClass}>
              <Row label="Vehicle Reg.No." value={vehicle.vehicleRegNo || "-"} />
              <Row label="Starting Date" value={result.startingDate || "-"} />
              <Row
                label="No. of Loads"
                value={toNum(result.noOfLoads).toFixed(2)}
              />
              <Row label="Fuel Type" value={vehicle.fuelTypeName || "-"} />
              <Row
                label="Fuel Rate"
                value={toNum(result.fuelRate).toFixed(2)}
              />
              <Row label="Fuel Qty" value={toNum(result.fuelQty).toFixed(2)} />
              <Row
                label="Fuel Total Amount"
                value={toNum(result.fuelTotalAmount).toFixed(4)}
              />
            </div>

            {/* RIGHT */}
            <div className={resultPanelClass}>
              <Row label="Ending Date" value={result.endingDate || "-"} />
              <Row label="No. of KM" value={toNum(result.noOfKm).toFixed(0)} />
              <Row label="No. of Hrs" value={toNum(result.noOfHrs).toFixed(0)} />

              <div className="mt-2 pt-2 border-t border-gray-200/30" />

              <Row
                label="Fuel Consum per KM"
                value={computed.fuelConsumPerKm.toFixed(8)}
                hint="( Fuel Qty / No.of Km )"
              />
              <Row label="Fuel Qty of Hr" value={toNum(result.fuelQty).toFixed(2)} />
              <Row
                label="Fuel Consum per Hrs"
                value={computed.fuelConsumPerHrs.toFixed(6)}
                hint="( Fuel Qty / No.of Hrs )"
              />
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              type="button"
              onClick={doReset}
              disabled={isBusy}
              className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg border ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaRedo />
              Reset
            </button>

            <button
              type="button"
              onClick={doSubmit}
              disabled={isBusy || !canSubmit}
              className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-white font-semibold shadow-lg ${
                isBusy || !canSubmit
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              <FaPaperPlane />
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default FuelConsumption;